import { parseIncomingWsMessage } from "./guards"
import type { IncomingMessage, OutgoingEnvelope } from "./types"
import { compilePdfTsLog, extractBackendTimeFields } from "./compile-pdf-ts-log"

const COMPILE_PDF_WS_TYPES = new Set([
  "compile_trigger",
  "compile_start",
  "compile_success",
  "compile_error",
  "pdf_ready",
  "file_change",
])

export interface CompilerWsClientHandlers {
  onOpen: () => void
  onClose: (event: CloseEvent) => void
  onMessage: (message: IncomingMessage) => void
  onError: (event: Event) => void
  onReconnectAttempt: (attempt: number, delayMs: number) => void
}

export class CompilerWsClient {
  private ws: WebSocket | null = null
  private reconnectTimer: number | null = null
  private url = ""
  private urlResolver: (() => Promise<string>) | null = null
  private disposed = false
  private reconnectAttempts = 0
  private connectionEpoch = 0
  /** Resolvers waiting for the next successful open after reconnect/connect. */
  private openWaiters: Array<{
    epoch: number
    resolve: (ok: boolean) => void
    timer: number
  }> = []

  constructor(
    private readonly handlers: CompilerWsClientHandlers,
    private readonly maxReconnectAttempts = 6,
  ) {}

  connect(url: string, urlResolver?: () => Promise<string>) {
    this.url = url
    this.urlResolver = urlResolver ?? null
    this.disposed = false
    this.clearReconnectTimer()
    this.openSocket()
  }

  disconnect() {
    this.disposed = true
    this.clearReconnectTimer()
    this.rejectOpenWaiters()
    if (this.ws) {
      this.ws.close()
      this.ws = null
    }
  }

  send<TData extends Record<string, unknown>>(message: OutgoingEnvelope<TData>): boolean {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      if (COMPILE_PDF_WS_TYPES.has(message.type)) {
        compilePdfTsLog("ws:send_skipped", `WS not open — cannot send ${message.type}`, {
          type: message.type,
          envelopeTimestamp: message.timestamp,
          readyState: this.ws?.readyState ?? null,
        })
      }
      return false
    }
    this.ws.send(JSON.stringify(message))
    if (COMPILE_PDF_WS_TYPES.has(message.type)) {
      compilePdfTsLog("ws:send", `wire send ${message.type}`, {
        type: message.type,
        envelopeTimestamp: message.timestamp,
        dataKeys: message.data ? Object.keys(message.data) : [],
        mainFile: typeof message.data?.main_file === "string" ? message.data.main_file : undefined,
        path: typeof message.data?.path === "string" ? message.data.path : undefined,
      })
    }
    return true
  }

  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN
  }

  getConnectionEpoch(): number {
    return this.connectionEpoch
  }

  /**
   * Force reconnect with a fresh URL.
   * Closes the current socket immediately so callers cannot accidentally send on a dying socket
   * while URL resolution is still in flight.
   */
  reconnect(): void {
    if (this.disposed) return
    this.clearReconnectTimer()
    this.reconnectAttempts = 0
    // Close immediately — do not leave the old socket open during async URL resolve.
    if (this.ws) {
      const closing = this.ws
      this.ws = null
      // Bump epoch so the closing socket's handlers are ignored.
      this.connectionEpoch += 1
      try {
        closing.onopen = null
        closing.onclose = null
        closing.onerror = null
        closing.onmessage = null
        closing.close()
      } catch {
        // ignore
      }
    }
    void this.openSocketWithResolvedUrl()
  }

  /**
   * Reconnect and wait until the new socket is OPEN (or timeout).
   * Prefer this over reconnect() + polling React wsStatus — that races with async URL resolve.
   */
  async reconnectAndWait(timeoutMs = 8000): Promise<boolean> {
    if (this.disposed) return false
    const epochBefore = this.connectionEpoch
    this.reconnect()
    return this.waitUntilOpenAfter(epochBefore, timeoutMs)
  }

  /** Wait until a socket opened after `epochBefore` becomes OPEN. */
  waitUntilOpenAfter(epochBefore: number, timeoutMs = 8000): Promise<boolean> {
    if (this.disposed) return Promise.resolve(false)
    if (this.isConnected() && this.connectionEpoch > epochBefore) {
      return Promise.resolve(true)
    }

    return new Promise<boolean>((resolve) => {
      const timer = window.setTimeout(() => {
        this.openWaiters = this.openWaiters.filter((w) => w.timer !== timer)
        resolve(this.isConnected() && this.connectionEpoch > epochBefore)
      }, timeoutMs)
      this.openWaiters.push({
        epoch: epochBefore,
        resolve: (ok) => {
          window.clearTimeout(timer)
          resolve(ok)
        },
        timer,
      })
    })
  }

  private resolveOpenWaiters() {
    if (!this.isConnected()) return
    const epoch = this.connectionEpoch
    const remaining: typeof this.openWaiters = []
    for (const w of this.openWaiters) {
      if (epoch > w.epoch) {
        window.clearTimeout(w.timer)
        w.resolve(true)
      } else {
        remaining.push(w)
      }
    }
    this.openWaiters = remaining
  }

  private rejectOpenWaiters() {
    const waiters = this.openWaiters
    this.openWaiters = []
    for (const w of waiters) {
      window.clearTimeout(w.timer)
      w.resolve(false)
    }
  }

  private async openSocketWithResolvedUrl(): Promise<void> {
    if (this.disposed) return
    if (this.urlResolver) {
      try {
        this.url = await this.urlResolver()
        console.log("[Compiler:WS] URL resolved for connect/reconnect")
      } catch (err) {
        console.warn("[Compiler:WS] urlResolver failed, using cached url", err)
      }
    }
    if (this.disposed) return
    this.openSocket()
  }

  private openSocket() {
    if (this.disposed || !this.url) return

    this.connectionEpoch += 1
    const epoch = this.connectionEpoch

    if (this.ws) {
      try {
        this.ws.onopen = null
        this.ws.onclose = null
        this.ws.onerror = null
        this.ws.onmessage = null
        this.ws.close()
      } catch {
        // ignore
      }
      this.ws = null
    }

    const socket = new WebSocket(this.url)
    this.ws = socket

    socket.onopen = () => {
      if (epoch !== this.connectionEpoch) return
      this.reconnectAttempts = 0
      this.handlers.onOpen()
      this.resolveOpenWaiters()
    }

    socket.onclose = (event) => {
      if (epoch !== this.connectionEpoch) return
      this.handlers.onClose(event)
      this.ws = null

      if (!this.disposed) {
        this.scheduleReconnect()
      }
    }

    socket.onerror = (event) => {
      if (epoch !== this.connectionEpoch) return
      this.handlers.onError(event)
    }

    socket.onmessage = (event) => {
      if (epoch !== this.connectionEpoch) return
      try {
        const payload: unknown = JSON.parse(event.data)
        const parsed = parseIncomingWsMessage(payload)
        if (!parsed) {
          this.handlers.onMessage({
            type: "error",
            data: { message: "Malformed WebSocket payload" },
          })
          return
        }
        if (COMPILE_PDF_WS_TYPES.has(parsed.type)) {
          const raw = payload && typeof payload === "object"
            ? (payload as Record<string, unknown>)
            : null
          compilePdfTsLog("ws:recv", `wire recv ${parsed.type}`, {
            type: parsed.type,
            envelopeTimestamp: typeof raw?.timestamp === "string" ? raw.timestamp : null,
            backendTimes: extractBackendTimeFields(parsed.data),
            data: parsed.data ?? null,
          })
        }
        this.handlers.onMessage(parsed)
      } catch {
        this.handlers.onMessage({
          type: "error",
          data: { message: "Failed to parse WebSocket message" },
        })
      }
    }
  }

  private scheduleReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) return
    this.reconnectAttempts += 1

    const exponential = Math.min(12000, 500 * 2 ** (this.reconnectAttempts - 1))
    const jitter = Math.floor(Math.random() * 250)
    const delayMs = exponential + jitter

    this.handlers.onReconnectAttempt(this.reconnectAttempts, delayMs)

    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null
      void this.openSocketWithResolvedUrl()
    }, delayMs)
  }

  private clearReconnectTimer() {
    if (this.reconnectTimer !== null) {
      window.clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
  }
}
